import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface UserGithubMappingDto {
  entityRef: string;
  email: string;
  githubUsername: string | null;
  revision: string;
}

export interface UpdateUserGithubMappingDto extends UserGithubMappingDto {
  pendingCatalogSync: boolean;
}

// Discover the plugin URL and use Backstage's authenticated fetch implementation.
export class UserManagementClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('user-management');
  }

  async getGithubUsernames(): Promise<{
    items: { entityRef: string; githubUsername: string | null }[];
  }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/github-usernames`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body?.error?.message ||
          `Failed to load GitHub mappings (HTTP ${response.status})`,
      );
    }
    return response.json();
  }

  async getGithubUsername(
    entityName: string,
    namespace: string = 'default',
  ): Promise<UserGithubMappingDto> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/users/${encodeURIComponent(
      entityName,
    )}/github-username?namespace=${encodeURIComponent(namespace)}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body?.error?.message ||
          `Failed to load GitHub username mapping (HTTP ${response.status})`,
      );
    }
    return response.json();
  }

  async updateGithubUsername(
    entityName: string,
    namespace: string = 'default',
    payload: { githubUsername?: string | null; revision: string },
  ): Promise<UpdateUserGithubMappingDto> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/users/${encodeURIComponent(
      entityName,
    )}/github-username?namespace=${encodeURIComponent(namespace)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send only mapping fields; HR profile data must remain read-only.
      body: JSON.stringify({
        githubUsername: payload.githubUsername ?? '',
        revision: payload.revision,
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body?.error?.message ||
          `Failed to save GitHub username (HTTP ${response.status})`,
      );
    }

    return response.json();
  }
}
